// src/pages/FinancePage/components/funnel-detail/utils.ts

import { Severity, UnitConceptRow, UnitConceptId, FunnelConceptRow, FunnelBlockId } from './types';

// ===== FORMATTERS =====

export function formatCurrency(value: number | null): string {
  if (value === null || isNaN(value)) return '—';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: 'COP',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(Math.abs(value));
}

export function formatCurrencySigned(value: number | null): string {
  if (value === null || isNaN(value)) return '—';
  if (value === 0) return '—';
  const sign = value > 0 ? '+' : '−';
  return `${sign}${formatCurrency(Math.abs(value))}`;
}

export function formatPercentage(value: number | null, decimals = 1): string {
  if (value === null || isNaN(value)) return '—';
  return `${value.toFixed(decimals)}%`;
}

export function formatPercentagePoints(value: number | null): string {
  if (value === null || isNaN(value)) return '—';
  if (Math.abs(value) < 0.05) return '—';
  const sign = value > 0 ? '+' : '−';
  return `${sign}${Math.abs(value).toFixed(1)}pp`;
}

export function formatMultiplier(value: number | null): string {
  if (value === null || isNaN(value)) return '—';
  return `${value.toFixed(2)}x`;
}

export function formatNumber(value: number | null): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('es-CO').format(value);
}

// ===== SEVERITY =====

export function getSeverity(real: number | null, target: number | null, isCostMetric: boolean): Severity {
  if (real === null || target === null) return 'no-data';
  if (target === 0 && real === 0) return 'neutral';
  const tolerance = Math.max(Math.abs(target) * 0.02, 1);
  const gap = isCostMetric ? real - target : target - real;
  if (Math.abs(gap) <= tolerance) return 'neutral';
  return gap > 0 ? 'bad' : 'good';
}

export function brechaTextColor(severity: Severity): string {
  switch (severity) {
    case 'good': return 'text-emerald-700';
    case 'bad': return 'text-red-700';
    case 'neutral': return 'text-gray-500';
    case 'no-data': return 'text-gray-300';
  }
}

const INCOME_LIKE_IDS: string[] = [
  'funnel-revenue',
  'unit-sale-price',
];

export function isCostConcept(id: string): boolean {
  return !INCOME_LIKE_IDS.includes(id);
}

// ===== TABLA 1: cálculo de valores derivados =====

export type UnitDerivedValues = {
  returnsAmount: number;
  totalCosts: number;
  effectivePrice: number;
  finalProfit: number;
  finalProfitPct: number;
};

// Si useTarget=true Y expectedProfitPct > 0, calcula iterativamente el precio de venta objetivo
// que cierra la ecuación: precio * (1 - utilidadEsperada) = costos + cpa.
// Si useTarget=false o expectedProfitPct=0, usa el sale-price del row directamente.
export function calculateUnitValues(
  rows: UnitConceptRow[],
  useTarget: boolean,
  expectedProfitPct: number = 0,
): UnitDerivedValues {
  const get = (id: UnitConceptId): number => {
    const row = rows.find(r => r.id === id);
    if (!row) return 0;
    return useTarget ? row.targetValue : (row.realValue ?? 0);
  };

  const productCost = get('unit-product-cost');
  const shippingCost = get('unit-shipping-cost');
  const realReturnCost = get('unit-returns-amount');
  const returnsRate = get('unit-returns-pct');
  const cpa = get('unit-cpa');

  const returnsAmount = useTarget
    ? shippingCost * returnsRate / 100
    : (realReturnCost > 0 ? realReturnCost : shippingCost * returnsRate / 100);

  let salePrice: number;

  if (useTarget && expectedProfitPct > 0 && expectedProfitPct < 100) {
    salePrice = (productCost + shippingCost + returnsAmount + cpa) / (1 - expectedProfitPct / 100);
  } else {
    salePrice = get('unit-sale-price');
  }

  const totalCosts = productCost + shippingCost + returnsAmount;
  const finalProfit = salePrice - totalCosts - cpa;
  const finalProfitPct = salePrice > 0 ? (finalProfit / salePrice) * 100 : 0;

  return {
    returnsAmount: Math.round(returnsAmount),
    totalCosts: Math.round(totalCosts),
    effectivePrice: Math.round(salePrice),
    finalProfit: Math.round(finalProfit),
    finalProfitPct,
  };
}

// ===== TABLA 2 (sin cambios) =====

export const FUNNEL_BLOCK_STYLES: Record<FunnelBlockId, {
  headerBg: string; headerHover: string; headerText: string; chevron: string; eyebrow: string;
}> = {
  'income': {
    headerBg: 'bg-emerald-50',
    headerHover: 'hover:bg-emerald-100',
    headerText: 'text-emerald-900',
    chevron: 'text-emerald-600',
    eyebrow: 'text-emerald-700',
  },
  'costs': {
    headerBg: 'bg-red-50',
    headerHover: 'hover:bg-red-100',
    headerText: 'text-red-900',
    chevron: 'text-red-600',
    eyebrow: 'text-red-700',
  },
  'cpa': {
    headerBg: 'bg-blue-50',
    headerHover: 'hover:bg-blue-100',
    headerText: 'text-blue-900',
    chevron: 'text-blue-600',
    eyebrow: 'text-blue-700',
  },
};

export function getFunnelBlockTotal(rows: FunnelConceptRow[], blockId: FunnelBlockId, useTarget: boolean): number {
  const sum = (ids: FunnelConceptRow['id'][]) => ids.reduce((acc, id) => {
    const row = rows.find(r => r.id === id);
    if (!row) return acc;
    return acc + (useTarget ? row.targetValue : (row.realValue ?? 0));
  }, 0);

  if (blockId === 'income') return sum(['funnel-revenue']);
  if (blockId === 'costs') return sum(['funnel-product-cost', 'funnel-freight', 'funnel-returns']);
  if (blockId === 'cpa') return sum(['funnel-cpa']);
  return 0;
}

export function getFunnelTotalProfit(rows: FunnelConceptRow[], useTarget: boolean): number {
  return getFunnelBlockTotal(rows, 'income', useTarget)
    - getFunnelBlockTotal(rows, 'costs', useTarget)
    - getFunnelBlockTotal(rows, 'cpa', useTarget);
}
